import React, { useEffect, useState } from 'react';
import { Database, RefreshCw, CheckCircle2, Clock, AlertCircle, Building2, Users, GraduationCap, Stethoscope, HardHat } from 'lucide-react';
import { migrationApi } from '../lib/api';

const StatusBadge = ({ status }) => {
  const map = {
    completed: { bg: '#dcfce7', color: '#15803d', label: 'COMPLETATA', icon: CheckCircle2 },
    scraping: { bg: '#dbeafe', color: '#1e40af', label: 'IN CORSO', icon: Clock },
    scraping_companies: { bg: '#dbeafe', color: '#1e40af', label: 'IN CORSO', icon: Clock },
    starting: { bg: '#fef7d4', color: '#854d0e', label: 'AVVIO', icon: Clock },
  };
  const s = map[status] || { bg: '#fee2e2', color: '#b91c1c', label: 'INATTIVA', icon: AlertCircle };
  const Icon = s.icon;
  return (
    <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold" style={{ background: s.bg, color: s.color }}>
      <Icon size={14} /> {s.label}
    </span>
  );
};

const MigrationPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      const r = await migrationApi.status();
      setData(r.data);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || 'Errore caricamento status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    if (!autoRefresh) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [autoRefresh]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-12 text-sm text-slate-400 gap-2">
        <div className="w-4 h-4 rounded-full border-2 border-[#023F80] border-t-transparent animate-spin" />
        Caricamento stato migrazione...
      </div>
    );
  }

  const state = data?.state || {};
  const counts = data?.counts || {};
  const total = state.companies_total || 0;
  const done = state.companies_done || 0;
  const percent = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <div className="space-y-5">
      <div className="bg-gradient-to-r from-[#023F80] to-[#023F80] rounded-2xl p-6 text-white relative overflow-hidden">
        <div className="absolute -right-10 -top-10 w-48 h-48 rounded-full bg-[#FFCB05]/20" />
        <div className="relative flex items-start justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center">
              <Database size={28} className="text-[#FFCB05]" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Migrazione dati da JOB81</h2>
              <p className="text-sm text-white/70 mt-1">Importazione automatica di aziende, lavoratori, formazione, visite e DPI</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <StatusBadge status={state.status} />
            <button
              onClick={load}
              className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
              title="Aggiorna ora"
            >
              <RefreshCw size={16} />
            </button>
          </div>
        </div>

        <div className="relative mt-6">
          <div className="flex items-baseline justify-between mb-2 text-sm">
            <span className="text-white/80">
              <span className="font-bold text-[#FFCB05]">{done}</span> di {total} aziende
            </span>
            <span className="font-bold text-lg">{percent}%</span>
          </div>
          <div className="h-3 bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full bg-[#FFCB05] transition-all duration-500"
              style={{ width: `${percent}%` }}
            />
          </div>
          {state.current && (
            <div className="mt-3 text-xs text-white/70">
              In elaborazione: <span className="font-semibold text-white">{state.current}</span>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <KpiBox icon={Building2} label="Aziende" value={counts.companies} accent="#023F80" bg="#dbeafe" />
        <KpiBox icon={Users} label="Lavoratori" value={counts.workers} accent="#023F80" bg="#e0e7ff" />
        <KpiBox icon={GraduationCap} label="Formazione" value={counts.formazione} accent="#15803d" bg="#dcfce7" />
        <KpiBox icon={Stethoscope} label="Visite mediche" value={counts.visite} accent="#854d0e" bg="#fef7d4" />
        <KpiBox icon={HardHat} label="DPI" value={counts.dpi} accent="#b91c1c" bg="#fee2e2" />
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-gray-800">Dettagli operazione</h3>
          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="accent-[#023F80]"
            />
            Aggiorna automaticamente (ogni 5s)
          </label>
        </div>

        <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-sm">
          <Row label="Stato">{state.status || '-'}</Row>
          <Row label="Ultimo aggiornamento">{state.updated_at ? new Date(state.updated_at).toLocaleString('it-IT') : '-'}</Row>
          <Row label="Aziende totali in JOB81">{total}</Row>
          <Row label="Aziende completate">{done}</Row>
          <Row label="Azienda corrente">{state.current || '-'}</Row>
          <Row label="Aziende registrate nel DB">{counts.companies}</Row>
        </dl>

        {error && (
          <div className="mt-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm">
            {error}
          </div>
        )}

        {state.status === 'completed' && (
          <div className="mt-5 p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm">
            <CheckCircle2 className="inline mr-2" size={16} />
            <strong>Migrazione completata!</strong> Tutti i dati disponibili sono stati importati in SafetyDesk81.
            Puoi ora consultarli nelle sezioni Aziende, Lavoratori, Formazione, Sorveglianza Sanitaria e DPI.
          </div>
        )}
      </div>
    </div>
  );
};

const KpiBox = ({ icon: Icon, label, value, accent, bg }) => (
  <div className="rounded-2xl p-4" style={{ background: bg }}>
    <div className="flex items-center justify-between">
      <Icon size={18} style={{ color: accent }} />
      <span className="text-[10px] uppercase tracking-widest font-semibold text-gray-600">{label}</span>
    </div>
    <div className="mt-2 text-3xl font-extrabold" style={{ color: accent }}>{value ?? 0}</div>
  </div>
);

const Row = ({ label, children }) => (
  <div className="flex justify-between items-center border-b border-slate-100 pb-2">
    <dt className="text-slate-500 text-xs uppercase tracking-wider font-semibold">{label}</dt>
    <dd className="text-slate-800 font-mono text-xs">{children}</dd>
  </div>
);

export default MigrationPage;
